import { eventBusCommandReceived } from 'features/application/actions';
import { LayoutMode } from 'features/layout/types';
import { User, UserId } from 'features/users/types';
import { selectLocalUser, selectLocalUserId, selectUserById } from 'features/users/usersSlice';
import {
  selectVirtualBackgroundEnforced,
  selectVirtualBackgrounds,
} from 'features/virtual-backgrounds/selectors';
import { store } from 'store/store';
import { logger } from 'utils/logger';
import { vbConfigToSDKState } from 'utils/eventBus/vbConfigToSDKState';
import {
  SelectedVirtualBackground,
  VirtualBackgroundFailureReason,
  VirtualBackgroundItem,
} from 'features/virtual-backgrounds/types';
import type { FeedStreamInfo } from 'utils/webrtc';
import type { FeedId } from 'features/streaming/types';

export type AnyFn = (...args: any[]) => any;

export interface BrandingOptionsConfig {
  paletteMode: 'dark' | 'light';
  primaryColor: string;
  toolbarColor: string;
  roomBackgroundColor: string;
}

export interface SDKInitState {
  // device config
  videoEnabled: boolean;
  audioEnabled: boolean;
  // user config
  username: string;
  initials: string;
  // layout config
  layoutMode: LayoutMode;
  showToolbar: boolean;
  showTopbar: boolean;
  showCaptions: boolean;

  // virtual backgrounds
  virtualBackground: Partial<VirtualBackgroundOptions>;
  virtualBackgrounds?: VirtualBackgroundItem[];
  replaceVirtualBackgrounds?: boolean;

  appLanguage: string;

  muteFrame: boolean;
  mediaDevices?: Partial<Record<MediaDeviceKind, string>>;

  requireRemoveUserConfirmation: boolean;

  baseDomain?: string;
  publicRoomUrl?: string;

  mobileScreenshare?: boolean;

  joinScreenEnabled?: boolean;
}

export interface QueuedEventListener {
  operation: EventTypes;
  event: string;
  target: 'document' | 'window';
}

export interface QueuedUICallback {
  operation: 'connectUICallback' | 'disconnectUICallback';
  name: UICallbackName;
}

export interface QueuedTileAction {
  operation: TileControlTypes;
  name: string;
  properties: TileActionProperties;
}

export interface SDKConnectionPayload extends Partial<SDKInitState> {
  eventListeners: QueuedEventListener[];
  UICallbacks: QueuedUICallback[];
  tileActions: QueuedTileAction[];
}

export type TileActionScope =
  'all' | 'remote' | 'local' | 'external' | 'screenshare-local' | 'screenshare-remote' | 'custom';

export type TileActionProperties = {
  label: string;
  scope: TileActionScope;
  // only support native samba icons. not recommended to use in this version;
  icon?: string;
};

export interface VirtualBackgroundOptions {
  enforce: boolean;
  blur: 'balanced' | 'strong';
  image: string;
  imageUrl: string;
  video: string;
  videoUrl: string;
  thumbnailUrl: string;
}

export type BroadcastMessageTypes =
  'enableVideo' | 'enableAudio' | 'disableVideo' | 'disableAudio' | 'toggleVideo' | 'toggleAudio';

export type MobileScreenshareMessageTypes = 'startMobileScreenshare' | 'stopMobileScreenshare';

export type MobileScreenshareData = {
  feedId: FeedId;
  streams: FeedStreamInfo[];
};

export type ScreenshareMessageTypes = 'startScreenshare' | 'stopScreenshare';

export type MuteFrameTypes = 'muteFrame' | 'unmuteFrame' | 'toggleMuteFrame';

export type TileControlTypes = 'addTileAction' | 'removeTileAction';

export type CustomTileTypes = 'addCustomTile' | 'removeCustomTile' | 'sendMessageToCustomTile';

type SendMessageType =
  | 'connected'
  | 'roomJoined'
  | 'userJoined'
  | 'userLeft'
  | 'userLeftBatch'
  | 'waitingUsersJoined'
  | 'waitingUsersLeft'
  | 'sessionEnded'
  | 'videoEnabled'
  | 'videoDisabled'
  | 'audioEnabled'
  | 'audioDisabled'
  | 'whiteboardOpened'
  | 'whiteboardClosed'
  | 'whiteboardCreated'
  | 'imageToWhiteboardAdded'
  | 'screenshareStarted'
  | 'screenshareStopped'
  | 'externalStreamStarted'
  | 'externalStreamStopped'
  | 'recordingStarted'
  | 'recordingStopped'
  | 'recordingFailed'
  | 'activeSpeakerChanged'
  | 'speakerStoppedTalking'
  | 'layoutModeChanged'
  | 'appError'
  | 'captionsEnabled'
  | 'captionsDisabled'
  | 'captionsSpokenLanguageChanged'
  | 'captionsFontSizeChanged'
  | 'permissionsChanged'
  | 'handRaised'
  | 'handLowered'
  | 'virtualBackgroundChanged'
  | 'virtualBackgroundDisabled'
  | 'localTileMaximized'
  | 'localTileMinimized'
  | 'userMaximized'
  | 'userMinimized'
  | 'internalMediaDeviceChanged'
  | 'mediaPermissionsFailed'
  | 'documentEvent'
  | 'appLanguageChanged'
  | 'UICallback'
  | 'roleChanged'
  | 'tileAction'
  | 'chatMessageReceived'
  | 'mobileMediaTokenUpdated'
  | 'mobileStartScreenshareRequested'
  | 'mobileStopScreenshareRequested'
  | 'connectionFailure'
  | 'mediaConnectionFailed'
  | 'restreamingFailed'
  | 'restreamingStarted'
  | 'restreamingStopped';

export type LocalTileTypes = 'minimizeLocalTile' | 'maximizeLocalTile';

export type ContentTypes = 'pinUser' | 'maximizeUser' | 'minimizeContent';

export type EventTypes = 'connectEventListener' | 'disconnectEventListener';

export type UICallbackName = 'leaveSession';

export type UICallbackTypes = 'connectUICallback' | 'disconnectUICallback';

export type ReceiveMessageType =
  | 'connect'
  | MobileScreenshareMessageTypes
  | BroadcastMessageTypes
  | ScreenshareMessageTypes
  | MuteFrameTypes
  | LocalTileTypes
  | ContentTypes
  | EventTypes
  | UICallbackTypes
  | TileControlTypes
  | CustomTileTypes
  | 'setTemplateParams'
  | 'createWhiteboard'
  | 'openWhiteboard'
  | 'closeWhiteboard'
  | 'toggleWhiteboard'
  | 'openLibraryFile'
  | 'closeLibraryFile'
  | 'toggleLibraryFile'
  | 'addImageToWhiteboard'
  | 'startRecording'
  | 'stopRecording'
  | 'addRecordingBookmark'
  | 'startRestreaming'
  | 'stopRestreaming'
  | 'showToolbar'
  | 'hideToolbar'
  | 'changeToolbarPosition'
  | 'toggleToolbar'
  | 'showTopbar'
  | 'hideTopbar'
  | 'toggleTopbar'
  | 'changeLayoutMode'
  | 'leaveSession'
  | 'endSession'
  | 'requestToggleAudio'
  | 'requestMute'
  | 'requestUnmute'
  | 'removeUser'
  | 'showCaptions'
  | 'hideCaptions'
  | 'toggleCaptions'
  | 'configureCaptions'
  | 'raiseHand'
  | 'lowerHand'
  | 'allowBroadcast'
  | 'disallowBroadcast'
  | 'allowScreenshare'
  | 'disallowScreenshare'
  | 'configureVirtualBackground'
  | 'disableVirtualBackground'
  | 'changeBrandingOptions'
  | 'changeRole'
  /**
   * @deprecated removed since sdk v0.0.51, still accepted for old embeds
   */
  | 'applyMediaDevices';

type EnrichFields = Partial<{
  userId: UserId;
  local?: boolean;
  localId?: boolean;
  virtualBackground?: SelectedVirtualBackground;
}>;

export type ReceiveMessage = { type: ReceiveMessageType; payload: any };

interface ErrorMessage {
  name: string;
  message: string;
  data?: any;
}

interface SendMessage {
  type: SendMessageType;
  data?: Partial<{
    user: User;
    userId: UserId;
    type: 'local' | 'remote';
    [key: string]: any;
  }>;
}

export interface StoredRoomState {
  appLanguage: string;
  media: {
    videoEnabled: boolean;
    audioEnabled: boolean;
  };
  layout: {
    mode: LayoutMode;
    showToolbar: boolean;
    showTopbar: boolean;
    toolbarPosition: 'left' | 'right' | 'bottom';
  };
  captionsState: {
    showCaptions: boolean;
    spokenLanguage: string;
    fontSize: string;
  };
  frameMuted: boolean;
  audioOnly: boolean;
}

const IGNORED_MESSAGE_TYPES = new Set(['iframe-pos']);

const NOT_APPLIED_MESSAGE: Record<VirtualBackgroundFailureReason, string> = {
  unsupported: 'Virtual background was not applied: this device cannot run virtual backgrounds',
  failed: 'Virtual background was not applied: applying it failed',
};

const CONNECT_USER_NAME_FIELDS = ['username', 'initials'] as const;

// connect carries the joining user's name, and the room's E2EE setting isn't known yet when it
// arrives, so the name never reaches the remote log from here.
const redactConnectUserName = (message: { type: string; data?: Partial<SDKInitState> }) => {
  if (message.type !== 'connect' || !message.data) {
    return message;
  }

  const data = { ...message.data };
  for (const field of CONNECT_USER_NAME_FIELDS) {
    if (field in data) {
      data[field] = '[Filtered]';
    }
  }

  return { ...message, data };
};

export class EventBus {
  allowedOrigin: string = '*';

  isEmbed = false;

  isNative = false;

  connected: boolean = false;

  initState: Partial<SDKInitState> = {};

  constructor() {
    const url = new URL(window.location.href);
    this.isEmbed = url.searchParams.has('dsEmbedFrame');

    if (this.isEmbed) {
      if (url.searchParams.has('dsEmbedFrameNative')) {
        this.isNative = true;
      }

      // @ts-ignore
      window.handleNativeMessage = (data: any) => this.onMessage({ data });
    }

    window.addEventListener('message', this.onMessage, false);
  }

  private onMessage = (event: MessageEvent<ReceiveMessage | any>) => {
    if (event.origin === window.location.origin) {
      // ignore dev server messages;
      return;
    }

    if (event.data.type && !IGNORED_MESSAGE_TYPES.has(event.data.type)) {
      if (event.data.type === 'connect' && event.origin !== 'null') {
        this.allowedOrigin = event.origin;
      }

      logger.remote().log('[SDK] command received:', redactConnectUserName(event.data));
      this.handleMessage(event.data);
    }
  };

  onConnection = (initState: Partial<SDKInitState>) => {
    // intent written before the first handshake survives it; every later connect is
    // authoritative for every field — `this.connected` is only ever set true, right below
    // (a connect arriving while one is in flight never reaches here — takeLeading drops it
    // upstream, and the SDK sends connect once per iframe document anyway)
    const { audioEnabled, videoEnabled } = this.connected ? {} : this.initState;

    this.initState = {
      ...(initState || {}),
      ...(audioEnabled !== undefined && { audioEnabled }),
      ...(videoEnabled !== undefined && { videoEnabled }),
    };

    this.connected = true;

    this.sendMessage('connected');
  };

  handleMessage = (message: ReceiveMessage) => {
    if (!this.isEmbed) {
      return;
    }

    if (store) {
      store.dispatch(eventBusCommandReceived(message));
    } else {
      console.error(message);
    }
  };

  error = (error: ErrorMessage) => {
    // Some refusals reach only the integrator; this is their only trace in the session log.
    if (this.isEmbed) {
      logger.remote().warn('[SDK] appError sent:', error);
    }
    this.sendMessage('appError', {}, error);
  };

  notAllowedError = (message: string, type: string, extraFields?: any) => {
    const data = { type };

    if (extraFields && typeof extraFields === 'object') {
      Object.assign(data, extraFields);
    }

    this.error({
      name: 'not-allowed',
      message,
      data,
    });
  };

  forbiddenActionError = (message: string, type: string, extraFields?: any) => {
    const data = { type };

    if (extraFields && typeof extraFields === 'object') {
      Object.assign(data, extraFields);
    }

    this.error({
      name: 'forbidden-action',
      message,
      data,
    });
  };

  // Not routed through enrichFields.virtualBackground: that path writes virtualBackgroundConfig
  // as a sibling of name/message, which is the wrong nesting for an error payload.
  virtualBackgroundNotApplied = (
    requested: SelectedVirtualBackground,
    reason: VirtualBackgroundFailureReason,
    // A request refused before the store recorded it never set the store's enforce flag.
    overrides: { message?: string; enforced?: boolean } = {}
  ) => {
    this.error({
      name: 'virtual-bg',
      message: overrides.message ?? NOT_APPLIED_MESSAGE[reason],
      data: {
        reason,
        virtualBackgroundConfig: {
          ...vbConfigToSDKState(requested, selectVirtualBackgrounds(store.getState())),
          enforced: overrides.enforced ?? selectVirtualBackgroundEnforced(store.getState()),
        },
      },
    });
  };

  sendMessage = <G>(type: SendMessageType, enrichFields?: EnrichFields, data?: G) => {
    if (this.isEmbed) {
      const message: SendMessage = this.enrichMesageFields(enrichFields, { type });

      if (message.data || data) {
        message.data = Object.assign(message.data || {}, data);
      }

      window.parent.postMessage(
        {
          DSPayload: message,
        },
        this.allowedOrigin
      );
    }
  };

  private enrichMesageFields = (enrichFields: EnrichFields | undefined, message: SendMessage) => {
    if (enrichFields?.userId) {
      message.data = {};

      const localUserId = selectLocalUserId(store.getState());
      message.data.user = selectUserById(store.getState(), enrichFields?.userId);

      message.data.type = localUserId === enrichFields.userId ? 'local' : 'remote';
    }

    if (enrichFields?.local) {
      message.data = {
        type: 'local',
      };

      message.data.user = selectLocalUser(store.getState());
    }

    if (enrichFields?.virtualBackground) {
      const virtualBackgrounds = selectVirtualBackgrounds(store.getState());
      message.data = {};
      message.data.virtualBackgroundConfig = vbConfigToSDKState(
        enrichFields.virtualBackground,
        virtualBackgrounds
      );

      if (message.data.virtualBackgroundConfig) {
        message.data.virtualBackgroundConfig.enforced = selectVirtualBackgroundEnforced(
          store.getState()
        );
      }
    }

    return message;
  };
}

export const eventBus = new EventBus();
